import { useMemo, useState } from "react";
import { Box } from "@mui/material";
import { ArrowDropUp, ArrowDropDown } from "@mui/icons-material";
import moment from "moment";
import { getSubscriptionPlanName, getMaxTrainees, isOverLimit } from "../../enums/SubscriptionPlans";
import { t } from "./coach/coachTokens";

const cell = {
  padding: "10px 12px",
  borderBottom: `1px solid ${t.borderLight}`,
  fontSize: "12.5px",
  verticalAlign: "middle",
  textAlign: "center",
};

const thBase = {
  textAlign: "center",
  padding: "10px 12px",
  fontSize: "10.5px",
  fontWeight: 700,
  color: t.t3,
  borderBottom: `1px solid ${t.border}`,
  background: t.bg,
  whiteSpace: "nowrap",
  textTransform: "uppercase",
  letterSpacing: ".06em",
  position: "sticky",
  top: 0,
  zIndex: 1,
};

const SortableTh = ({ label, sortKey, currentKey, currentDir, onSortChange, align }) => {
  const active = currentKey === sortKey;
  const effectiveAlign = align || "center";
  const flexJustify =
    effectiveAlign === "center" ? "center" : effectiveAlign === "start" ? "flex-start" : "flex-end";
  return (
    <Box
      component="th"
      onClick={() => onSortChange(sortKey)}
      sx={{
        ...thBase,
        textAlign: effectiveAlign,
        cursor: "pointer",
        userSelect: "none",
        color: active ? t.text : t.t3,
        "&:hover": { color: t.text },
      }}
    >
      <Box sx={{ display: "inline-flex", alignItems: "center", justifyContent: flexJustify, gap: "2px" }}>
        {label}
        <Box sx={{ display: "inline-flex", flexDirection: "column", lineHeight: 0.6, opacity: active ? 1 : 0.35 }}>
          <ArrowDropUp sx={{ fontSize: 14, color: active && currentDir === "ASC" ? t.green : t.t3, mb: "-4px" }} />
          <ArrowDropDown sx={{ fontSize: 14, color: active && currentDir === "DESC" ? t.green : t.t3, mt: "-4px" }} />
        </Box>
      </Box>
    </Box>
  );
};

// Two top-level tabs: a companies overview and a per-company resource-count
// breakdown. Each resource has its own count column on the resources tab.
export const COMPANY_TABS = [
  { key: "companies", label: "חברות" },
  { key: "resources", label: "משאבים" },
];

// Resource count columns shown on the "resources" tab.
const RESOURCE_COLS = [
  { key: "exercises", label: "תרגילים", get: (c) => c.exerciseCount || 0 },
  { key: "menus", label: "תפריטים", get: (c) => c.menuCount || 0 },
  { key: "templates", label: "תבניות אימון", get: (c) => c.programTemplateCount || 0 },
  { key: "checkins", label: "צ׳ק-אינס", get: (c) => c.checkInCount || 0 },
  { key: "guides", label: "מדריכים", get: (c) => c.guideCount || 0 },
  { key: "forms", label: "טפסים", get: (c) => c.formCount || 0 },
];

// Derives the sortable value for a company row by column key.
const sortValue = (c, key) => {
  switch (key) {
    case "name":
      return (c.name || "").toLowerCase();
    case "subscription":
      return c.subscriptionPlan || "";
    case "trainers":
      return c.trainerCount || 0;
    case "trainees":
      return c.totalTraineeCount || 0;
    case "lastRegistered":
      // epoch seconds (see render comment); raw value sorts fine as-is.
      return c.lastTraineeRegisteredDate || 0;
    case "whiteLabel":
      return c.bundleId ? 1 : 0;
    default: {
      // Resource column keys (exercises/menus/templates/checkins).
      const col = RESOURCE_COLS.find((rc) => rc.key === key);
      return col ? col.get(c) : 0;
    }
  }
};

const CompaniesTable = ({ companies, onCompanySelect, tab = "companies", showSubscription = true }) => {
  const isResources = tab === "resources";
  const [sortKey, setSortKey] = useState("trainees");
  const [sortDir, setSortDir] = useState("DESC");

  const onSortChange = (key) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "ASC" ? "DESC" : "ASC"));
    } else {
      setSortKey(key);
      setSortDir(key === "name" ? "ASC" : "DESC");
    }
  };

  const sorted = useMemo(() => {
    const list = [...(companies || [])];
    list.sort((a, b) => {
      const av = sortValue(a, sortKey);
      const bv = sortValue(b, sortKey);
      let cmp;
      if (typeof av === "string") cmp = av.localeCompare(bv, "he");
      else cmp = av - bv;
      return sortDir === "ASC" ? cmp : -cmp;
    });
    return list;
  }, [companies, sortKey, sortDir]);

  if (!companies || companies.length === 0) {
    return (
      <Box sx={{ padding: "48px", textAlign: "center", color: t.t3, fontSize: "13px" }}>
        אין חברות להצגה
      </Box>
    );
  }

  return (
    <Box component="table" sx={{ width: "100%", borderCollapse: "collapse" }}>
      <thead>
        <tr>
          <SortableTh label="חברה" sortKey="name" currentKey={sortKey} currentDir={sortDir} onSortChange={onSortChange} align="start" />
          {isResources ? (
            RESOURCE_COLS.map((rc) => (
              <SortableTh
                key={rc.key}
                label={rc.label}
                sortKey={rc.key}
                currentKey={sortKey}
                currentDir={sortDir}
                onSortChange={onSortChange}
              />
            ))
          ) : (
            <>
              {showSubscription && (
                <SortableTh label="מנוי" sortKey="subscription" currentKey={sortKey} currentDir={sortDir} onSortChange={onSortChange} />
              )}
              <SortableTh label="מאמנים" sortKey="trainers" currentKey={sortKey} currentDir={sortDir} onSortChange={onSortChange} />
              <SortableTh label="מתאמנים" sortKey="trainees" currentKey={sortKey} currentDir={sortDir} onSortChange={onSortChange} />
              <SortableTh label="ממותג" sortKey="whiteLabel" currentKey={sortKey} currentDir={sortDir} onSortChange={onSortChange} />
              <SortableTh label="מתאמן אחרון שנרשם" sortKey="lastRegistered" currentKey={sortKey} currentDir={sortDir} onSortChange={onSortChange} />
            </>
          )}
        </tr>
      </thead>
      <tbody>
        {sorted.map((c) => {
          const planLabel = getSubscriptionPlanName(c.subscriptionPlan);
          const maxT = getMaxTrainees(c.subscriptionPlan);
          // Subscription usage is measured on ACTIVE trainees, not the total.
          const activeCount = c.activeTraineeCount ?? 0;
          const overLimit = isOverLimit(c.subscriptionPlan, activeCount);
          // Backend serializes Instant as epoch SECONDS (JavaTimeModule, timestamps
          // not disabled) — same as company.createdDate elsewhere. Use moment.unix.
          const lastRegistered = c.lastTraineeRegisteredDate
            ? moment.unix(c.lastTraineeRegisteredDate).format("DD/MM/YY")
            : null;
          return (
            <Box
              component="tr"
              key={c.id}
              onClick={() => onCompanySelect?.(c.id)}
              sx={{
                cursor: "pointer",
                transition: "background .1s, opacity .15s",
                opacity: c.enabled ? 1 : 0.55,
                "&:hover td": { background: t.greenLight },
              }}
            >
              {/* Company name */}
              <Box component="td" sx={{ ...cell, whiteSpace: "nowrap", textAlign: "start" }}>
                <Box sx={{ fontWeight: 700, color: t.text, fontSize: "13px" }}>{c.name}</Box>
              </Box>

              {isResources ? (
                RESOURCE_COLS.map((rc) => (
                  <Box component="td" key={rc.key} sx={cell}>
                    <Box component="span" sx={{ fontWeight: 700, color: t.text, fontSize: 13 }}>
                      {(rc.get(c) || 0).toLocaleString()}
                    </Box>
                  </Box>
                ))
              ) : (
                <>
                  {/* Subscription plan + capacity (matches the Companies page) */}
                  {showSubscription && (
                    <Box component="td" sx={cell}>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", alignItems: "center" }}>
                        <Box component="span" sx={{ fontSize: 12, fontWeight: 500, color: t.t2 }}>
                          {planLabel}
                        </Box>
                        <Box
                          component="span"
                          dir="ltr"
                          sx={{ fontSize: 11, fontWeight: overLimit ? 700 : 500, color: overLimit ? t.redText : t.t3 }}
                        >
                          {activeCount} / {maxT ? maxT : "∞"}
                        </Box>
                      </Box>
                    </Box>
                  )}

                  {/* Trainers */}
                  <Box component="td" sx={cell}>
                    <Box component="span" sx={{ fontWeight: 600, color: t.text }}>{c.trainerCount ?? 0}</Box>
                  </Box>

                  {/* Trainees (total · active) */}
                  <Box component="td" sx={cell}>
                    <Box component="span" dir="ltr" sx={{ display: "inline-block" }}>
                      <Box component="strong" sx={{ color: t.text }}>{c.totalTraineeCount ?? 0}</Box>
                      {c.activeTraineeCount != null && (
                        <Box component="span" sx={{ color: t.t2, fontSize: 11 }}>
                          {" "}
                          · {c.activeTraineeCount} פעילים
                        </Box>
                      )}
                    </Box>
                  </Box>

                  {/* White-label (has a bundle id) */}
                  <Box component="td" sx={cell}>
                    {c.bundleId ? (
                      <Box
                        component="span"
                        sx={{
                          fontSize: 9.5,
                          fontWeight: 700,
                          padding: "2px 9px",
                          borderRadius: "10px",
                          whiteSpace: "nowrap",
                          lineHeight: 1.6,
                          background: t.greenLight,
                          color: t.greenText,
                          border: "1px solid rgba(1,187,167,.2)",
                        }}
                      >
                        White Label
                      </Box>
                    ) : (
                      <Box component="span" sx={{ color: t.t3 }}>–</Box>
                    )}
                  </Box>

                  {/* Last trainee registration date */}
                  <Box component="td" sx={cell}>
                    {lastRegistered ? (
                      <Box component="span" dir="ltr" sx={{ fontSize: 12, color: t.t2 }}>{lastRegistered}</Box>
                    ) : (
                      <Box component="span" sx={{ color: t.t3 }}>–</Box>
                    )}
                  </Box>
                </>
              )}
            </Box>
          );
        })}
      </tbody>
    </Box>
  );
};

export default CompaniesTable;
